import { useLayoutEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useRootAttributes, useTheme } from '../theme/ThemeRoot';

/**
 * Portal that mounts into its own `.nyk-ui-root` (same theme/font size) so overlays stay styled inside any host.
 *
 * `container` overrides the theme's portal container for this one portal: it is how the header's actions can be placed
 * in a host's own title bar without the rest of the overlays following them there.
 */
export function Portal({ children, container }: { children: ReactNode; container?: HTMLElement | null }) {
  const { portalContainer } = useTheme();
  const host = container ?? portalContainer;
  const attrs = useRootAttributes();
  const [target, setTarget] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    const parent = host ?? (typeof document !== 'undefined' ? document.body : null);
    if (!parent) return;
    const el = document.createElement('div');
    for (const [k, v] of Object.entries(attrs))
      if (v != null) el.setAttribute(k === 'className' ? 'class' : k, v);
    el.style.background = 'transparent';
    parent.appendChild(el);
    setTarget(el);
    return () => {
      parent.removeChild(el);
      setTarget(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [host, attrs['data-theme'], attrs['data-font-size']]);
  return target ? createPortal(children, target) : null;
}
